/**
 * CanvasWalkthrough — a lightweight, zero-dep, on-canvas guided tour for the
 * Layout Builder. Designed for:
 *
 *   1) First-time organizers on the SeatPilot dashboard
 *   2) New organizers whose ticketing company embedded the Builder via our SDK
 *
 * Steps are anchored to elements by `data-walkthrough` attribute so we never
 * break when class names change. If an anchor is missing, the step is skipped.
 *
 * Persistence: a "seen" flag is stored in localStorage per session; the Help
 * button on the top bar can re-trigger the tour anytime.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { X, ChevronRight, ChevronLeft, CheckCircle2, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'sp_canvas_walkthrough_seen_v1';

/**
 * Replace `{{brand}}` tokens in a step list so embedding ticketing companies
 * see their own brand instead of "the Layout Builder" / "your Dashboard".
 * Example: applyBrand(STEPS_BUILDER, 'TicketKart') →
 *   "Welcome to TicketKart's seat builder", "Welcome to TicketKart Dashboard", …
 */
function applyBrand(steps, brandName) {
  // Replace `{{brand}}` with the trimmed brand name when supplied. When no
  // brand is provided we strip the placeholder cleanly (including any
  // surrounding whitespace) so users never see the literal "{{brand}}"
  // token in the tour text.
  const safe = String(brandName || '').trim().slice(0, 60);
  if (!safe) {
    return steps.map(s => ({
      ...s,
      title: (s.title || '').replace(/\s*\{\{brand\}\}\s*/g, ' ').trim(),
      body: (s.body || '').replace(/\s*\{\{brand\}\}\s*/g, ' ').trim(),
    }));
  }
  return steps.map(s => ({
    ...s,
    title: (s.title || '').replace(/\{\{brand\}\}/g, safe),
    body: (s.body || '').replace(/\{\{brand\}\}/g, safe),
  }));
}

const STEPS_BUILDER = [
  {
    anchor: null,
    title: "Welcome to {{brand}}",
    body:
      "This is the same Canvas your end-customers will see. In under a minute, you'll add seats, set prices, publish, and share a widget link. Ready?",
    align: 'center',
  },
  {
    anchor: 'walkthrough-toolbox',
    title: 'Step 1 — Pick a tool',
    body:
      'Click any item to add it to the canvas. Start with "Row" or "Multiple Rows" for theatre-style seating, or "Bus" to build a coach instantly.',
    align: 'right',
  },
  {
    anchor: 'walkthrough-canvas',
    title: 'Step 2 — Position it on the canvas',
    body:
      'Drag the placed shape to move it. Use the corner handles to resize. Shift-click to select multiple objects. Grid snap keeps everything aligned.',
    align: 'center',
  },
  {
    anchor: 'walkthrough-properties',
    title: 'Step 3 — Edit properties',
    body:
      'Select any shape and its properties appear on the right — seat count, row label, category, price. Changes save automatically.',
    align: 'left',
  },
  {
    anchor: 'walkthrough-properties',
    title: 'Step 4 — Set categories & prices',
    body:
      'Assign every seat a category (VIP, Standard, Economy…) and a price right in the same properties panel. The checkout widget uses these automatically.',
    align: 'left',
  },
  {
    anchor: 'walkthrough-autosave',
    title: 'Step 5 — Auto-save is always on',
    body:
      "A green dot means your changes are safe. You don't need to click Save — it runs every 5 seconds. Press Ctrl/⌘+S to force save.",
    align: 'bottom',
  },
  {
    anchor: 'walkthrough-publish',
    title: 'Step 6 — Publish & share',
    body:
      "When you're done, click Publish. You'll get a widget URL you can embed on your website, share with customers, or call from your API.",
    align: 'bottom',
  },
  {
    anchor: null,
    title: "You're ready to build with {{brand}}",
    body:
      "Need help again? Click the \"?\" button on the top bar to replay this tour any time. Happy seating!",
    align: 'center',
    finish: true,
  },
];

const STEPS_DASHBOARD = [
  {
    anchor: null,
    title: 'Welcome to your {{brand}} Dashboard',
    body: "This is your control center for everything seating-related — layouts, customers, payments, API keys, analytics. Let's take 30 seconds to show you around.",
    align: 'center',
  },
  {
    anchor: 'walkthrough-tab-layouts',
    title: 'Step 1 — Layouts',
    body: 'Create, edit, and publish your seating layouts here. Mix theatre rows, banquet tables, bus seating, GA zones — anything your venues need.',
    align: 'right',
  },
  {
    anchor: 'walkthrough-tab-bookings',
    title: 'Step 2 — Bookings',
    body: 'Every seat sold via your widget or SDK lands here. Filter by event, organiser, status. Confirm holds, refund, export to CSV.',
    align: 'right',
  },
  {
    anchor: 'walkthrough-tab-api-keys',
    title: 'Step 3 — API Keys',
    body: 'Generate keys to embed our SDK on your website. Each key isolates one integration — production, staging, mobile app, etc.',
    align: 'right',
  },
  {
    anchor: 'walkthrough-tab-analytics',
    title: 'Step 4 — Analytics',
    body: 'Live revenue, top events, geo distribution, conversion funnel. All powered by Recharts — drillable, exportable.',
    align: 'right',
  },
  {
    anchor: 'walkthrough-tab-billing',
    title: 'Step 5 — Billing',
    body: 'Your subscription, invoices, payment methods, monthly seat usage. Upgrade or change plans anytime.',
    align: 'right',
  },
  {
    anchor: null,
    title: "Ready to roll with {{brand}}!",
    body: "Click any tab above to dive in, or hit Layouts → New Layout to build your first one. The 'Help' button on every page replays the tour.",
    align: 'center',
    finish: true,
  },
];

// Backwards-compat alias used by existing import in AdvancedLayoutBuilder.js
const STEPS = STEPS_BUILDER;

export default function CanvasWalkthrough({ open, onClose, forceShow = false, steps = STEPS_BUILDER, brandName = '' }) {
  // Apply brand substitution once per (steps, brandName) pair.
  // useMemo is critical: without it, applyBrand() returns a fresh array each
  // render, causing useLayoutEffect (which depends on effectiveSteps) to fire
  // infinitely after clicking "Next".
  const effectiveSteps = useMemo(
    () => applyBrand(steps, brandName),
    [steps, brandName]
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [anchorRect, setAnchorRect] = useState(null);
  const tooltipRef = useRef(null);

  // Measure anchor element position whenever step changes
  useLayoutEffect(() => {
    if (!open) return;
    const step = effectiveSteps[stepIndex];
    if (!step?.anchor) {
      setAnchorRect(null);
      return;
    }
    const el = document.querySelector(`[data-walkthrough="${step.anchor}"]`);
    if (!el) {
      // Anchor missing — skip to next step automatically
      const t = setTimeout(() => setStepIndex((i) => Math.min(i + 1, effectiveSteps.length - 1)), 300);
      return () => clearTimeout(t);
    }
    const measure = () => {
      const r = el.getBoundingClientRect();
      setAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, stepIndex, effectiveSteps]);

  // Keyboard: esc to close, arrows to navigate
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
      else if (e.key === 'ArrowRight') handleNext();
      else if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, stepIndex]); // stepIndex included so handlers close over latest value

  if (!open) return null;

  const step = effectiveSteps[stepIndex];
  const total = effectiveSteps.length;

  const handleClose = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch (err) { /* quota/security errors ignored */ void err; }
    onClose?.();
  };
  const handleNext = () => {
    if (stepIndex >= total - 1) return handleClose();
    setStepIndex(stepIndex + 1);
  };
  const handlePrev = () => setStepIndex(Math.max(0, stepIndex - 1));
  const handleSkip = () => handleClose();

  // Position the tooltip
  let tooltipStyle = {};
  if (anchorRect) {
    const margin = 16;
    const tooltipWidth = 360;
    const tooltipHeight = 190;
    const align = step.align || 'right';
    const viewportPad = 12;

    let top = anchorRect.top;
    let left = anchorRect.left + anchorRect.width + margin;

    if (align === 'left') {
      left = Math.max(viewportPad, anchorRect.left - tooltipWidth - margin);
    } else if (align === 'bottom') {
      top = anchorRect.top + anchorRect.height + margin;
      left = Math.max(viewportPad, anchorRect.left);
    } else if (align === 'right') {
      // default already set
    }

    // Clamp to viewport
    if (left + tooltipWidth > window.innerWidth - viewportPad) {
      left = Math.max(viewportPad, window.innerWidth - tooltipWidth - viewportPad);
    }
    if (top + tooltipHeight > window.innerHeight - viewportPad) {
      top = Math.max(viewportPad, window.innerHeight - tooltipHeight - viewportPad);
    }
    tooltipStyle = { top, left, width: tooltipWidth };
  }

  return (
    <div
      data-testid="canvas-walkthrough"
      className="fixed inset-0 z-[9999] pointer-events-none"
      style={{ animation: 'fadeIn 200ms ease-out' }}
    >
      {/* Dim backdrop with spotlight cutout on the anchor */}
      <div
        className="absolute inset-0 pointer-events-auto"
        onClick={handleSkip}
        style={{
          background: anchorRect
            ? `radial-gradient(ellipse ${Math.max(anchorRect.width, 120)}px ${Math.max(anchorRect.height, 80)}px at ${anchorRect.left + anchorRect.width / 2}px ${anchorRect.top + anchorRect.height / 2}px, transparent 60%, rgba(15, 23, 42, 0.72) 61%)`
            : 'rgba(15, 23, 42, 0.55)',
          transition: 'background 250ms ease-out',
        }}
      />

      {/* Highlight ring around the anchor */}
      {anchorRect && (
        <div
          className="absolute pointer-events-none"
          style={{
            top: anchorRect.top - 6,
            left: anchorRect.left - 6,
            width: anchorRect.width + 12,
            height: anchorRect.height + 12,
            border: '2px solid #8b5cf6',
            borderRadius: 10,
            boxShadow: '0 0 0 4px rgba(139, 92, 246, 0.25), 0 0 24px rgba(139, 92, 246, 0.4)',
            animation: 'walkthroughPulse 1.8s ease-in-out infinite',
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute pointer-events-auto bg-white rounded-xl shadow-2xl border border-violet-200"
        style={
          anchorRect
            ? tooltipStyle
            : {
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 420,
              }
        }
        data-testid="canvas-walkthrough-tooltip"
      >
        <div className="flex items-start justify-between p-4 pb-2">
          <div className="flex items-center gap-2">
            <span className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-white text-xs font-bold flex items-center justify-center">
              {step.finish ? <CheckCircle2 className="w-4 h-4" /> : stepIndex}
            </span>
            <h3 className="text-sm font-semibold text-slate-900">{step.title}</h3>
          </div>
          <button
            onClick={handleSkip}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Close walkthrough"
            data-testid="canvas-walkthrough-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 pb-3">
          <p className="text-sm text-slate-600 leading-relaxed">{step.body}</p>
        </div>

        {/* Progress bar */}
        <div className="px-4 pb-3">
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-violet-500 to-indigo-600 transition-all duration-300"
              style={{ width: `${((stepIndex + 1) / total) * 100}%` }}
            />
          </div>
          <p className="text-[10px] text-slate-400 mt-1">
            Step {stepIndex + 1} of {total}
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 p-3 bg-slate-50 rounded-b-xl border-t border-slate-100">
          <button
            onClick={handleSkip}
            className="text-xs text-slate-500 hover:text-slate-700"
            data-testid="canvas-walkthrough-skip"
          >
            Skip tour
          </button>
          <div className="flex gap-1">
            <button
              onClick={handlePrev}
              disabled={stepIndex === 0}
              className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              data-testid="canvas-walkthrough-prev"
            >
              <ChevronLeft className="w-3 h-3" /> Back
            </button>
            <button
              onClick={handleNext}
              className="text-xs px-3 py-1.5 rounded-md bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:shadow-md flex items-center gap-1"
              data-testid="canvas-walkthrough-next"
            >
              {step.finish ? (
                <>Done <Sparkles className="w-3 h-3" /></>
              ) : stepIndex === total - 1 ? (
                <>Finish <CheckCircle2 className="w-3 h-3" /></>
              ) : (
                <>Next <ChevronRight className="w-3 h-3" /></>
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes walkthroughPulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.25), 0 0 24px rgba(139, 92, 246, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(139, 92, 246, 0.18), 0 0 32px rgba(139, 92, 246, 0.55); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/**
 * Helper hook — returns `{open, close, replay}` controls.
 * Auto-opens the walkthrough on first visit unless user has dismissed it.
 *
 * @param {object} opts
 * @param {boolean} [opts.autoOpenOnFirstVisit=true]
 * @param {string}  [opts.storageKey] — separate flag per location (dashboard vs builder)
 */
export function useCanvasWalkthrough({ autoOpenOnFirstVisit = true, storageKey } = {}) {
  const [open, setOpen] = useState(false);
  const key = storageKey || STORAGE_KEY;
  useEffect(() => {
    if (!autoOpenOnFirstVisit) return;
    let seen = false;
    try {
      seen = localStorage.getItem(key) === '1';
    } catch (err) { void err; }
    if (!seen) {
      // Give the page a moment to render its anchors
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, [autoOpenOnFirstVisit, key]);

  const close = () => {
    try { localStorage.setItem(key, '1'); } catch (err) { void err; }
    setOpen(false);
  };
  const replay = () => {
    try { localStorage.removeItem(key); } catch (err) { void err; }
    setOpen(false);
    setTimeout(() => setOpen(true), 100);
  };

  return { open, close, replay };
}

// Pre-built step lists for callers
export { STEPS_BUILDER, STEPS_DASHBOARD };
